import { PLATFORM } from '@/types/enums/common';

const STORAGE_KEYS = {
  USER_INFO: 'BELINK-USER-INFO',
  COMMON_PARAMS: 'BELINK-COMMON-PARAMS',
  WELFARE_UNION_ID: 'BELINK-WELFARE-UNION-ID',
} as const;

/** 平台专属参数（按平台区分存储） */
export interface IPlatformParams {
  appId: string;
  openId: string;
}

export interface IUserInfoParams {
  token?: string;
  userId: string;
  unionId: string;
  /** 当前平台的 openId（向后兼容，用于登录接口调用） */
  openId: string;
  nickname: string;
  avatar: string;
  userStoreId: string;
  userStoreName: string;
  managerStoreId: string;
  managerStoreName: string;
  isBlocked: boolean;
  isNewUser: boolean;
  /** 是否为快照用户（服务端返回，允许页面正常访问） */
  isSnapShotUser?: boolean;
  /** 是否为店长 */
  isStoreManager?: boolean;

  /** H5/公众号平台参数 */
  h5?: IPlatformParams;
  /** 小程序平台参数 */
  mini?: IPlatformParams;
  /** APP 平台参数 */
  app?: IPlatformParams;
  thirdPartyAuthRecord?: {
    unionId?: string;
    appId?: string;
    mobile?: string;
  };
}

/** 公共参数 */
export interface ICommonParams {
  /** 微信 appID（当前平台） */
  appId: string | '';
  /** 平台类型 */
  platform: string | PLATFORM.H5;
  /** 字体大小设置 */
  fontSizeSetting: string | '';
  /** 入口域名 */
  entryDomain: string | '';
  /** 认证域名 */
  authDomain: string | '';
  /** 直播域名 */
  liveDomain: string | '';
}

// ===================== 用户信息 =====================
export const getUserInfoLocal = (): IUserInfoParams | null => {
  try {
    const stored = localStorage.getItem(STORAGE_KEYS.USER_INFO);
    if (stored) {
      return JSON.parse(stored);
    }
    return null;
  } catch (error) {
    return null;
  }
};

export const setUserInfoLocal = (userInfo: IUserInfoParams) => {
  localStorage.setItem(STORAGE_KEYS.USER_INFO, JSON.stringify(userInfo));
};

export const updateUserInfoLocal = (partialUserInfo: Partial<IUserInfoParams>) => {
  const currentUserInfo = getUserInfoLocal();
  if (currentUserInfo) {
    setUserInfoLocal({ ...currentUserInfo, ...partialUserInfo });
  }
};

export const clearUserInfoLocal = () => {
  localStorage.removeItem(STORAGE_KEYS.USER_INFO);
};

// ===================== 通用路由参数 =====================
export const getCommonParamsLocal = (): ICommonParams | null => {
  try {
    const stored = localStorage.getItem(STORAGE_KEYS.COMMON_PARAMS);
    if (stored) {
      return JSON.parse(stored);
    }
    return null;
  } catch (error) {
    return null;
  }
};

export const setCommonParamsLocal = (params: ICommonParams) => {
  localStorage.setItem(STORAGE_KEYS.COMMON_PARAMS, JSON.stringify(params));
};

export const updateCommonParamsLocal = (partialParams: Partial<ICommonParams>) => {
  const currentParams = getCommonParamsLocal();
  if (currentParams) {
    setCommonParamsLocal({ ...currentParams, ...partialParams });
  }
};

export const clearCommonParamsLocal = () => {
  localStorage.removeItem(STORAGE_KEYS.COMMON_PARAMS);
};

// ===================== OpenId 平台工具函数 =====================

type PlatformKey = 'h5' | 'mini' | 'app';

/**
 * 根据平台获取对应的平台 key
 */
export function getPlatformKey(platform: string): PlatformKey {
  switch (platform) {
    case PLATFORM.APP:
      return 'app';
    case PLATFORM.MINI:
      return 'mini';
    case PLATFORM.H5:
    default:
      return 'h5';
  }
}

/**
 * 根据平台从用户信息中获取对应的 openId
 * 注意：不会 fallback 到 userInfo.openId，如果对应平台没有 openId 则返回空字符串
 * 这样 H5 平台在没有自己的 openId 时会触发微信授权流程，而不是使用其他平台的 openId
 */
export function getOpenIdByPlatform(userInfo: IUserInfoParams | null, platform: string): string {
  if (!userInfo) return '';
  const key = getPlatformKey(platform);
  return userInfo[key]?.openId || '';
}

/**
 * 根据平台从用户信息中获取对应的 appId
 */
export function getAppIdByPlatform(userInfo: IUserInfoParams | null, platform: string): string {
  if (!userInfo) return '';
  const key = getPlatformKey(platform);
  return userInfo[key]?.appId || '';
}

/**
 * 获取指定平台的完整参数
 */
export function getPlatformParams(userInfo: IUserInfoParams | null, platform: string): IPlatformParams | null {
  if (!userInfo) return null;
  const key = getPlatformKey(platform);
  return userInfo[key] || null;
}

/**
 * 更新用户信息中对应平台的参数
 */
export function updatePlatformParams(platform: string, params: Partial<IPlatformParams>): void {
  const currentUserInfo = getUserInfoLocal();
  if (!currentUserInfo) return;

  const key = getPlatformKey(platform);
  const currentPlatformParams = currentUserInfo[key] || { appId: '', openId: '' };

  updateUserInfoLocal({
    [key]: { ...currentPlatformParams, ...params },
    // 同时更新主 openId 为当前平台的（向后兼容）
    ...(params.openId ? { openId: params.openId } : {}),
  });
}

// ==================== 向后兼容函数（逐步废弃） ====================

/**
 * @deprecated 请使用 getPlatformKey 代替
 */
export function getOpenIdKeyByPlatform(platform: string): 'h5' | 'mini' | 'app' {
  return getPlatformKey(platform);
}

/**
 * @deprecated 请使用 updatePlatformParams 代替
 */
export function updatePlatformOpenId(platform: string, openId: string): void {
  updatePlatformParams(platform, { openId });
}

// ===================== 福利官 UnionId =====================
/**
 * 获取福利官 UnionId
 * 用于记录通过福利官分享进入的商品卡片中的福利官 unionId
 */
export const getWelfareUnionIdLocal = (): string | null => {
  try {
    return localStorage.getItem(STORAGE_KEYS.WELFARE_UNION_ID);
  } catch (error) {
    return null;
  }
};

/**
 * 设置福利官 UnionId
 * @param unionId 福利官的 unionId，如果为空字符串则清除
 */
export const setWelfareUnionIdLocal = (unionId: string) => {
  if (unionId) {
    localStorage.setItem(STORAGE_KEYS.WELFARE_UNION_ID, unionId);
  } else {
    clearWelfareUnionIdLocal();
  }
};

/**
 * 清除福利官 UnionId
 */
export const clearWelfareUnionIdLocal = () => {
  localStorage.removeItem(STORAGE_KEYS.WELFARE_UNION_ID);
};
